import type { ReactNode } from 'react';
import {
  useActivityFeed,
  useDashboardCharts,
  useDashboardKpis,
  useDashboardSummary,
  useDashboardToday,
  useOverdueInvoices,
  useTeamAllocationGrid,
  useTeamTasks,
  useUpcomingPayments,
} from '../../../hooks/useDashboard';
import { useI18n } from '../../../contexts/I18nContext';
import { KpiCard, buildKpiConfig, type KpiKind } from '../KpiCards';
import { IncomeExpenseCard, RevenueDonutCard } from '../ChartsSection';
import { TodayPanel } from '../TodayPanel';
import { UpcomingPayments } from '../UpcomingPayments';
import { OverdueInvoicesPanel } from '../OverdueInvoicesPanel';
import { TeamAllocationGrid } from '../TeamAllocationGrid';
import { TeamTasksPanel } from '../TeamTasksPanel';
import { ProjectStatusGrid } from '../ProjectStatusGrid';
import { ActivityFeedPanel } from '../ActivityFeedPanel';
import { FadeIn, Skeleton, SkeletonCardGrid, SkeletonList } from '../../ui/Skeleton';

// ---------------------------------------------------------------------------
// Self-contained dashboard widgets. Each widget fetches its own data with the
// existing react-query hooks (dedupe keeps this at one request per endpoint,
// no matter how many widgets share it), shows the same skeleton + error UI the
// monolithic DashboardPage used, and renders the existing section component.
// ---------------------------------------------------------------------------

export function SectionError({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-400">
      {message}
    </div>
  );
}

interface QueryLike<T> {
  isLoading: boolean;
  isError: boolean;
  data: T | undefined;
}

/**
 * Factorizes the isLoading / isError / FadeIn pattern the old page repeated
 * eight times. `errorKey` is the i18n key for the section's error message.
 */
function QueryBoundary<T>({
  query,
  errorKey,
  skeleton,
  children,
}: {
  query: QueryLike<T>;
  errorKey: string;
  skeleton: ReactNode;
  children: (data: T) => ReactNode;
}) {
  const { t } = useI18n();
  if (query.isLoading) return <>{skeleton}</>;
  if (query.isError) return <SectionError message={t(errorKey)} />;
  if (query.data === undefined) return null;
  return <FadeIn>{children(query.data)}</FadeIn>;
}

// --- KPI cards (one widget per card) ----------------------------------------

function KpiWidget({ kind }: { kind: KpiKind }) {
  const kpis = useDashboardKpis();
  // Sparklines are opportunistic: the card renders without them until the
  // charts query resolves (same behavior as the old combined section).
  const charts = useDashboardCharts();
  return (
    <QueryBoundary
      query={kpis}
      errorKey="dashboard.error.kpis"
      skeleton={<Skeleton height={92} className="rounded-xl" />}
    >
      {(data) => <KpiCard card={buildKpiConfig(kind, data, charts.data)} />}
    </QueryBoundary>
  );
}

export function KpiRevenueWidget() {
  return <KpiWidget kind="revenue" />;
}
export function KpiExpensesWidget() {
  return <KpiWidget kind="expenses" />;
}
export function KpiProfitWidget() {
  return <KpiWidget kind="profit" />;
}
export function KpiReceivablesWidget() {
  return <KpiWidget kind="receivables" />;
}
export function KpiPayablesWidget() {
  return <KpiWidget kind="payables" />;
}
export function KpiUrgentTasksWidget() {
  return <KpiWidget kind="urgentTasks" />;
}

// --- Charts ------------------------------------------------------------------

export function RevenueDonutWidget() {
  const charts = useDashboardCharts();
  return (
    <QueryBoundary
      query={charts}
      errorKey="dashboard.error.charts"
      skeleton={<Skeleton height={320} className="rounded-2xl" />}
    >
      {(data) => <RevenueDonutCard charts={data} />}
    </QueryBoundary>
  );
}

export function IncomeExpenseWidget() {
  const charts = useDashboardCharts();
  return (
    <QueryBoundary
      query={charts}
      errorKey="dashboard.error.charts"
      skeleton={<Skeleton height={320} className="rounded-2xl" />}
    >
      {(data) => <IncomeExpenseCard charts={data} />}
    </QueryBoundary>
  );
}

// --- Sections ------------------------------------------------------------------

export function TodayWidget() {
  const today = useDashboardToday();
  return (
    <QueryBoundary
      query={today}
      errorKey="dashboard.error.today"
      skeleton={
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton height={220} className="rounded-xl" />
          <Skeleton height={220} className="rounded-xl" />
        </div>
      }
    >
      {(data) => <TodayPanel today={data} />}
    </QueryBoundary>
  );
}

export function UpcomingPaymentsWidget() {
  const upcoming = useUpcomingPayments(14);
  return (
    <QueryBoundary
      query={upcoming}
      errorKey="dashboard.error.upcoming"
      skeleton={<Skeleton height={260} className="rounded-xl" />}
    >
      {(data) => <UpcomingPayments data={data} />}
    </QueryBoundary>
  );
}

export function OverdueInvoicesWidget() {
  const overdue = useOverdueInvoices();
  return (
    <QueryBoundary
      query={overdue}
      errorKey="dashboard.error.overdueInvoices"
      skeleton={<Skeleton height={260} className="rounded-xl" />}
    >
      {(data) => <OverdueInvoicesPanel data={data} />}
    </QueryBoundary>
  );
}

export function TeamAllocationWidget() {
  const team = useTeamAllocationGrid();
  return (
    <QueryBoundary
      query={team}
      errorKey="dashboard.error.team"
      skeleton={<Skeleton height={260} className="rounded-xl" />}
    >
      {(data) => <TeamAllocationGrid entries={data} />}
    </QueryBoundary>
  );
}

export function TeamTasksWidget() {
  const teamTasks = useTeamTasks();
  return (
    <QueryBoundary
      query={teamTasks}
      errorKey="dashboard.error.teamTasks"
      skeleton={<Skeleton height={300} className="rounded-2xl" />}
    >
      {(data) => <TeamTasksPanel members={data} />}
    </QueryBoundary>
  );
}

export function ProjectsStatusWidget() {
  const summary = useDashboardSummary({ status: 'ACTIVE' });
  return (
    <QueryBoundary
      query={summary}
      errorKey="dashboard.error.projects"
      skeleton={
        <div className="rounded-xl border border-border bg-card p-5 shadow-card">
          <SkeletonCardGrid count={6} showProgress />
        </div>
      }
    >
      {(data) => <ProjectStatusGrid projects={data.projects} />}
    </QueryBoundary>
  );
}

export function ActivityFeedWidget() {
  const activity = useActivityFeed(15);
  return (
    <QueryBoundary
      query={activity}
      errorKey="dashboard.error.activity"
      skeleton={
        <div className="rounded-xl border border-border bg-card p-5 shadow-card">
          <SkeletonList rows={6} />
        </div>
      }
    >
      {(data) => <ActivityFeedPanel items={data} />}
    </QueryBoundary>
  );
}
